import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { api, extractError } from "../lib/api";
import { PageHeader, SectionCard } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth, isAdmin } from "../context/AuthContext";
import { isProductionLoadingSupervisorRole } from "../lib/dashboardRole";
import { useWS } from "../context/WebSocketContext";
import { formatIndiaDateTime } from "../lib/indiaTime";

const ALL = "__all__";
const REPROCESS_OUTPUT_CATEGORIES = [
  { value: "PRIMARY_GOODS", label: "Primary goods" },
  { value: "COPRODUCT_NAKKU", label: "Coproduct — nakku" },
  { value: "REJECTION", label: "Rejection" },
  { value: "BRAN", label: "Bran" },
];

function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isOpenReprocessStatus(status) {
  const s = String(status || "").toUpperCase();
  return s === "PLANNED" || s === "IN_PROGRESS" || s === "IN_PROCESS";
}

function batchStatusLabel(status) {
  if (status === "UNLOADING_ACTIVE") return "Unloading";
  if (status === "MILLING_ONLY") return "Milling";
  if (status === "ARCHIVED_COMPLETED") return "Archived";
  return status || "—";
}

/** B-01 1509 SATHI White Sella */
function batchLabel(b) {
  if (!b) return "—";
  if (b.batch_label || b.label) return b.batch_label || b.label;
  return [b.code || b.batch_code, b.crop_name, b.type_name].filter(Boolean).join(" ") || "—";
}

export default function PackingRegisterPage() {
  const { user } = useAuth();
  const adminUser = isAdmin(user);
  const packingDesk = isProductionLoadingSupervisorRole(user?.role);
  const { subscribe } = useWS();

  const [tab, setTab] = useState("log");
  const [batches, setBatches] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [items, setItems] = useState([]);
  const [crops, setCrops] = useState([]);
  const [riceTypes, setRiceTypes] = useState([]);
  const [logs, setLogs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [allStock, setAllStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    batch_id: "",
    crop_id: "",
    type_id: "",
    item_id: "",
    bags_packed: "",
    weight_per_bag: "",
    entry_kind: "PACKING",
    notes: "",
  });
  const [historyFilters, setHistoryFilters] = useState({
    batch_id: ALL,
    date_from: "",
    date_to: "",
  });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    batch_id: "",
    assign_mode: "batch", // batch | presystem
    crop_id: "",
    type_id: "",
    item_id: "",
    bags_packed: "",
    weight_per_bag: "",
    notes: "",
  });
  const [editSaving, setEditSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Daily reprocess / blend output (same API as Rice Reprocessing page)
  const [reprocessRuns, setReprocessRuns] = useState([]);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [reprocessSubmitting, setReprocessSubmitting] = useState(false);
  const [reprocessForm, setReprocessForm] = useState({
    reprocessing_batch_id: "",
    output_date: todayLocal(),
    item_id: "",
    pack_size_kg: "50",
    bags_packed: "",
    output_category: "PRIMARY_GOODS",
    target_production_batch_id: "",
  });

  // Admin may pick archived batches and enter opening/closing stock.
  const logBatchOptions = adminUser ? allBatches : batches;
  const isOpeningStock = adminUser && form.entry_kind === "OPENING_STOCK";
  const openReprocessRuns = reprocessRuns.filter((r) => isOpenReprocessStatus(r.status));
  const selectedReprocessRun =
    openReprocessRuns.find((r) => String(r.id) === String(reprocessForm.reprocessing_batch_id)) || null;

  const refreshStock = useCallback(async () => {
    const stockRes = await api.get("/production/inventory", { params: { positive_only: 1 } });
    setAllStock(stockRes.data?.results || []);
  }, []);

  const loadBatchDetail = useCallback(async (batchId) => {
    if (!batchId) {
      setLogs([]);
      setInventory([]);
      return;
    }
    const [logsRes, invRes] = await Promise.all([
      api.get("/production/logs", { params: { batch_id: Number(batchId) } }),
      api.get(`/production/batches/${batchId}/inventory`),
    ]);
    setLogs(logsRes.data?.results || []);
    setInventory(invRes.data?.results || []);
  }, []);

  const loadHistory = useCallback(async () => {
    const params = {};
    if (historyFilters.batch_id !== ALL) params.batch_id = Number(historyFilters.batch_id);
    if (historyFilters.date_from) params.date_from = historyFilters.date_from;
    if (historyFilters.date_to) params.date_to = historyFilters.date_to;
    const { data } = await api.get("/production/logs", { params });
    setLogs(data?.results || []);
  }, [historyFilters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, allBRes, iRes, cropRes, typeRes] = await Promise.all([
        api.get("/production/batches", { params: { open_only: 1 } }),
        api.get("/production/batches"),
        api.get("/production/masters/items"),
        api.get("/production/masters/crops"),
        api.get("/production/masters/rice-types"),
      ]);
      const batchRows = bRes.data?.results || [];
      const itemRows = iRes.data?.results || [];
      const allBatchRows = allBRes.data?.results || [];
      const cropRows = cropRes.data?.results || [];
      const typeRows = typeRes.data?.results || [];
      setBatches(batchRows);
      setAllBatches(allBatchRows);
      setItems(itemRows);
      setCrops(cropRows);
      setRiceTypes(typeRows);

      const defaultBatch = batchRows[0] || (adminUser ? allBatchRows[0] : null);
      setForm((f) => {
        const next = { ...f };
        if (!next.batch_id && defaultBatch) next.batch_id = String(defaultBatch.id);
        if (!next.item_id && itemRows[0]) next.item_id = String(itemRows[0].id);
        if (!next.crop_id && cropRows[0]) next.crop_id = String(cropRows[0].id);
        if (!next.type_id && typeRows[0]) next.type_id = String(typeRows[0].id);
        return next;
      });

      await refreshStock();
      const batchId = form.batch_id || (defaultBatch ? String(defaultBatch.id) : "");
      if (batchId) await loadBatchDetail(batchId);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [adminUser, loadBatchDetail, refreshStock]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsub1 = subscribe("production", load);
    const unsub2 = subscribe("production_batch", load);
    const unsub3 = subscribe("outward_dispatch", () => {
      refreshStock().catch(() => {});
    });
    return () => {
      unsub1?.();
      unsub2?.();
      unsub3?.();
    };
  }, [subscribe, load, refreshStock]);

  useEffect(() => {
    if (tab !== "history") return;
    loadHistory().catch((e) => toast.error(extractError(e)));
  }, [tab, loadHistory]);

  useEffect(() => {
    if (tab !== "log" || !form.batch_id) return;
    loadBatchDetail(form.batch_id).catch((e) => toast.error(extractError(e)));
  }, [tab, form.batch_id, loadBatchDetail]);

  useEffect(() => {
    if (tab !== "stock") return;
    refreshStock().catch((e) => toast.error(extractError(e)));
  }, [tab, refreshStock]);

  const loadReprocessRuns = useCallback(async () => {
    setReprocessLoading(true);
    try {
      const { data } = await api.get("/production/reprocessing", { params: { limit: 50 } });
      const rows = data?.results || [];
      setReprocessRuns(rows);
      const open = rows.filter((r) => isOpenReprocessStatus(r.status));
      setReprocessForm((f) => {
        const next = { ...f };
        if (!next.reprocessing_batch_id && open[0]) {
          next.reprocessing_batch_id = String(open[0].id);
          next.target_production_batch_id = open[0].target_production_batch_id
            ? String(open[0].target_production_batch_id)
            : f.target_production_batch_id || form.batch_id || "";
        }
        if (!next.item_id && items[0]) next.item_id = String(items[0].id);
        if (!next.target_production_batch_id && form.batch_id) {
          next.target_production_batch_id = form.batch_id;
        }
        return next;
      });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setReprocessLoading(false);
    }
  }, [form.batch_id, items]);

  useEffect(() => {
    if (tab !== "reprocess") return;
    loadReprocessRuns().catch(() => {});
  }, [tab, loadReprocessRuns]);

  const submitReprocessOutput = async (e) => {
    e.preventDefault();
    if (!reprocessForm.reprocessing_batch_id) {
      return toast.error("Select an active reprocessing run");
    }
    if (!reprocessForm.item_id) return toast.error("Select an output item");
    const bags = Number(reprocessForm.bags_packed) || 0;
    if (bags <= 0) return toast.error("Enter bags packed / blended");
    const wpb = Number(reprocessForm.pack_size_kg) || 0;
    const qtl = bags > 0 && wpb > 0 ? Math.round(((bags * wpb) / 100) * 1000) / 1000 : 0;
    setReprocessSubmitting(true);
    try {
      const { data } = await api.post("/production/reprocessing/output-log", {
        reprocessing_batch_id: Number(reprocessForm.reprocessing_batch_id),
        output_date: reprocessForm.output_date || todayLocal(),
        item_id: Number(reprocessForm.item_id),
        pack_size_kg: wpb,
        bags_packed: bags,
        quintals_packed: qtl,
        output_category: reprocessForm.output_category || "PRIMARY_GOODS",
        target_production_batch_id: reprocessForm.target_production_batch_id
          ? Number(reprocessForm.target_production_batch_id)
          : null,
      });
      toast.success(`Reprocess output logged — ${bags} bags credited`);
      setReprocessForm((f) => ({ ...f, bags_packed: "" }));
      if (data?.batch) {
        setReprocessRuns((prev) =>
          prev.map((r) => (r.id === data.batch.id ? data.batch : r))
        );
      }
      await Promise.all([refreshStock(), loadReprocessRuns()]);
      if (reprocessForm.target_production_batch_id) {
        await loadBatchDetail(reprocessForm.target_production_batch_id);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setReprocessSubmitting(false);
    }
  };

  const onBatchChange = (v) => {
    setForm((f) => ({ ...f, batch_id: v }));
    loadBatchDetail(v).catch((e) => toast.error(extractError(e)));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id) return toast.error("Select an item");
    const entryKind = adminUser ? (form.entry_kind || "PACKING") : "PACKING";
    if (entryKind === "OPENING_STOCK") {
      if (!form.crop_id) return toast.error("Select a crop");
      if (!form.type_id) return toast.error("Select a rice type / variety");
    } else if (!form.batch_id) {
      return toast.error("Select a batch");
    }
    setSubmitting(true);
    try {
      const payload = {
        item_id: Number(form.item_id),
        bags_packed: Number(form.bags_packed),
        weight_per_bag: Number(form.weight_per_bag),
        entry_kind: entryKind,
        notes: form.notes || undefined,
      };
      if (entryKind === "OPENING_STOCK") {
        payload.crop_id = Number(form.crop_id);
        payload.type_id = Number(form.type_id);
        // Never send batch_id here — server finds/creates the pre-system OS-… batch.
      } else {
        payload.batch_id = Number(form.batch_id);
      }
      const { data: row } = await api.post("/production/logs", payload);
      toast.success(
        entryKind === "OPENING_STOCK"
          ? "Closing / opening stock added — inventory updated"
          : "Packaging logged — stack inventory updated",
      );
      const nextBatchId = row?.batch_id ? String(row.batch_id) : form.batch_id;
      setForm((f) => ({
        ...f,
        bags_packed: "",
        weight_per_bag: "",
        notes: "",
        ...(nextBatchId ? { batch_id: nextBatchId } : {}),
      }));
      // Reload batches so newly created OS-… batch appears in filters / lists.
      const allBRes = await api.get("/production/batches");
      setAllBatches(allBRes.data?.results || []);
      if (nextBatchId) await loadBatchDetail(nextBatchId);
      await refreshStock();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    const isOs = Boolean(row.is_opening_stock_batch) || row.entry_kind === "OPENING_STOCK";
    setEditForm({
      batch_id: String(row.batch_id || ""),
      assign_mode: isOs ? "presystem" : "batch",
      crop_id: row.crop_id != null ? String(row.crop_id) : (crops[0] ? String(crops[0].id) : ""),
      type_id: row.type_id != null ? String(row.type_id) : (riceTypes[0] ? String(riceTypes[0].id) : ""),
      item_id: String(row.item_id),
      bags_packed: String(row.bags_packed ?? ""),
      weight_per_bag: String(row.weight_per_bag ?? ""),
      notes: row.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!editForm.item_id) return toast.error("Select an item");
    const toPresystem = adminUser && editForm.assign_mode === "presystem";
    if (toPresystem) {
      if (!editForm.crop_id) return toast.error("Select a crop");
      if (!editForm.type_id) return toast.error("Select a rice type / variety");
    } else if (!editForm.batch_id) {
      return toast.error("Select a batch");
    }
    setEditSaving(true);
    try {
      const prevBatchId = String(editRow.batch_id || "");
      const payload = {
        item_id: Number(editForm.item_id),
        bags_packed: Number(editForm.bags_packed),
        weight_per_bag: Number(editForm.weight_per_bag),
        notes: editForm.notes,
      };
      if (toPresystem) {
        payload.crop_id = Number(editForm.crop_id);
        payload.type_id = Number(editForm.type_id);
        payload.entry_kind = "OPENING_STOCK";
      } else {
        payload.batch_id = Number(editForm.batch_id);
      }
      const { data: after } = await api.put(`/production/logs/${editRow.id}`, payload);
      const nextBatchId = after?.batch_id != null ? String(after.batch_id) : editForm.batch_id;
      toast.success(
        prevBatchId !== nextBatchId
          ? toPresystem
            ? `Moved to pre-system batch ${after?.batch_code || "OS-…"}`
            : "Packaging log moved to the selected batch"
          : "Packaging log updated",
      );
      setEditRow(null);
      const allBRes = await api.get("/production/batches");
      setAllBatches(allBRes.data?.results || []);
      if (tab === "history") await loadHistory();
      else if (form.batch_id) await loadBatchDetail(form.batch_id);
      await refreshStock();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setEditSaving(false);
    }
  };

  const deleteLog = async (row) => {
    if (
      !window.confirm(
        `Delete packaging log for ${row.item_name || "item"} — ${row.bags_packed} bags @ ${row.weight_per_bag} kg?\n\nStock will be reduced. Blocked if bags were already loaded out.`,
      )
    ) {
      return;
    }
    setDeletingId(row.id);
    try {
      await api.delete(`/production/logs/${row.id}`);
      toast.success("Packaging log deleted");
      if (tab === "history") await loadHistory();
      else if (form.batch_id) await loadBatchDetail(form.batch_id);
      await refreshStock();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="packing-register-page">
      <PageHeader
        eyebrow={packingDesk ? "Packing & Loading" : "Production · Packing"}
        title={packingDesk ? "Packing" : "Packing register"}
        description="Log milling packing, reprocess/blend output, review stock, and browse history — same floor desk."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/stacks">{packingDesk ? "Stock on hand" : "Inventory"}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/production/reprocessing">Reprocessing runs</Link>
            </Button>
            {adminUser ? (
              <Button asChild variant="outline" size="sm">
                <Link to="/production/batches">Batch controller</Link>
              </Button>
            ) : null}
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-card border border-border p-1 h-auto flex-wrap">
          <TabsTrigger value="log" className="text-xs sm:text-sm">Log packaging</TabsTrigger>
          <TabsTrigger value="reprocess" className="text-xs sm:text-sm">Reprocess output</TabsTrigger>
          <TabsTrigger value="stock" className="text-xs sm:text-sm">Stock on hand</TabsTrigger>
          <TabsTrigger value="history" className="text-xs sm:text-sm">History</TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-4 space-y-4">
          <SectionCard title="New packaging entry" testId="packing-form">
            {loading && logBatchOptions.length === 0 && crops.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
            ) : !isOpeningStock && logBatchOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No open batches. Admin must plan a batch and start paddy unloading (or leave a batch in milling-only).
                {adminUser ? (
                  <>
                    {" "}
                    Or switch entry type to{" "}
                    <button
                      type="button"
                      className="text-primary underline"
                      onClick={() => setForm((f) => ({ ...f, entry_kind: "OPENING_STOCK" }))}
                    >
                      Opening / closing stock
                    </button>{" "}
                    to add godown stock for a crop without a milling batch.
                  </>
                ) : null}
              </p>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                {adminUser ? (
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Entry type</Label>
                    <Select
                      value={form.entry_kind || "PACKING"}
                      onValueChange={(v) =>
                        setForm((f) => ({
                          ...f,
                          entry_kind: v,
                          // Avoid leaving a milling batch selected — that used to credit the wrong batch.
                          ...(v === "OPENING_STOCK" ? { batch_id: "" } : {}),
                        }))
                      }
                    >
                      <SelectTrigger className="mt-1" data-testid="pack-entry-kind">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PACKING">Packing (normal)</SelectItem>
                        <SelectItem value="OPENING_STOCK">
                          Opening / closing stock (before packing system)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {isOpeningStock ? (
                      <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
                        For godown closing stock where milling batches were not used. Pick crop and type —
                        the system creates a reusable archived stock batch (OS-…) and credits inventory.
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {isOpeningStock ? (
                  <>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Crop</Label>
                      <Select
                        value={form.crop_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, crop_id: v }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="pack-crop">
                          <SelectValue placeholder="Select crop" />
                        </SelectTrigger>
                        <SelectContent>
                          {crops.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name || c.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        Type / variety
                      </Label>
                      <Select
                        value={form.type_id}
                        onValueChange={(v) => setForm((f) => ({ ...f, type_id: v }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="pack-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {riceTypes.map((t) => (
                            <SelectItem key={t.id} value={String(t.id)}>
                              {t.name || t.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
                    <Select value={form.batch_id} onValueChange={onBatchChange}>
                      <SelectTrigger className="mt-1" data-testid="pack-batch">
                        <SelectValue placeholder="Select batch" />
                      </SelectTrigger>
                      <SelectContent>
                        {logBatchOptions.map((b) => (
                          <SelectItem key={b.id} value={String(b.id)}>
                            {batchLabel(b)} · {batchStatusLabel(b.status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {adminUser ? (
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Admin can select archived batches for corrections or opening stock.
                      </p>
                    ) : null}
                  </div>
                )}
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Item</Label>
                  <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="pack-item">
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((it) => (
                        <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    {isOpeningStock ? "Bags (closing stock)" : "Bags packed"}
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    className="mt-1"
                    value={form.bags_packed}
                    onChange={(e) => setForm({ ...form, bags_packed: e.target.value })}
                    required
                    data-testid="pack-bags"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Weight / bag (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="mt-1"
                    value={form.weight_per_bag}
                    onChange={(e) => setForm({ ...form, weight_per_bag: e.target.value })}
                    required
                    data-testid="pack-wpb"
                    placeholder="50 or 25"
                  />
                </div>
                {adminUser ? (
                  <div className="md:col-span-2 lg:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</Label>
                    <Input
                      className="mt-1"
                      value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder={
                        isOpeningStock
                          ? "e.g. Closing stock as on go-live date"
                          : "Optional"
                      }
                      data-testid="pack-notes"
                    />
                  </div>
                ) : null}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-10"
                  data-testid="pack-submit"
                >
                  {submitting
                    ? "Saving…"
                    : isOpeningStock
                      ? "Add closing stock"
                      : "Log packaging"}
                </Button>
              </form>
            )}
          </SectionCard>

          {form.batch_id ? (
            <SectionCard title="This batch — on hand by pack size" testId="packing-inventory">
              {inventory.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No inventory for this batch yet.</p>
              ) : (
                <div className="flex flex-wrap gap-3">
                  {inventory.map((row) => (
                    <div key={row.id} className="rounded-md border border-border px-4 py-3 min-w-[140px]">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide">{row.item_name}</p>
                      <p className="text-lg font-semibold tabular">{row.bags_on_hand} bags</p>
                      <p className="text-xs text-muted-foreground">{row.weight_per_bag} kg/bag</p>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          ) : null}

          {form.batch_id ? (
            <SectionCard title={`This batch — recent packaging (${logs.length})`} testId="packing-batch-logs">
              {adminUser ? (
                <p className="text-xs text-muted-foreground mb-3">
                  Edit or delete a wrong entry here. Delete is blocked if those bags were already loaded out.
                </p>
              ) : null}
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No packaging logged for this batch yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Time</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Item</th>
                        <th className="text-right py-2 px-2">Bags</th>
                        <th className="text-right py-2 px-2">Kg/bag</th>
                        <th className="text-right py-2 px-2">Qtl</th>
                        <th className="text-left py-2 px-2">By</th>
                        {adminUser ? <th className="text-right py-2 px-2">Actions</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((row) => (
                        <tr key={row.id} className="border-b border-border/50">
                          <td className="py-2 px-2 text-xs text-muted-foreground whitespace-nowrap">
                            {formatIndiaDateTime(row.created_at) || "—"}
                          </td>
                          <td className="py-2 px-2 text-xs">
                            {row.entry_kind === "OPENING_STOCK" ? "Opening stock" : "Packing"}
                          </td>
                          <td className="py-2 px-2">{row.item_name}</td>
                          <td className="py-2 px-2 text-right tabular">{row.bags_packed}</td>
                          <td className="py-2 px-2 text-right tabular">{row.weight_per_bag}</td>
                          <td className="py-2 px-2 text-right tabular">{row.total_weight_qtl}</td>
                          <td className="py-2 px-2 text-xs">{row.created_by}</td>
                          {adminUser ? (
                            <td className="py-2 px-2 text-right whitespace-nowrap">
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                title="Edit packaging log"
                                aria-label="Edit packaging log"
                                onClick={() => openEdit(row)}
                                data-testid={`edit-pack-log-batch-${row.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-muted-foreground hover:text-red-700"
                                title="Delete packaging log"
                                aria-label="Delete packaging log"
                                disabled={deletingId === row.id}
                                onClick={() => deleteLog(row)}
                                data-testid={`delete-pack-log-batch-${row.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          ) : null}
        </TabsContent>

        <TabsContent value="reprocess" className="mt-4 space-y-4">
          <SectionCard title="Daily reprocess / blend output" testId="packing-reprocess-output">
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              Log packed or in-line blended output against an open reprocessing run. Stock is credited to the
              target milling batch immediately (same as the Reprocessing page). Start / cut / reconcile runs on{" "}
              <Link to="/production/reprocessing" className="text-primary underline">
                Reprocessing &amp; blending
              </Link>
              .
            </p>
            {reprocessLoading && openReprocessRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Loading reprocessing runs…</p>
            ) : openReprocessRuns.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">
                No open reprocessing runs.{" "}
                <Link to="/production/reprocessing" className="text-primary underline">
                  Create a run and log cutting
                </Link>{" "}
                first, then return here to pack / blend output.
              </p>
            ) : (
              <form
                onSubmit={submitReprocessOutput}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end"
              >
                <div className="md:col-span-2 lg:col-span-3">
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Reprocessing run
                  </Label>
                  <Select
                    value={reprocessForm.reprocessing_batch_id}
                    onValueChange={(v) => {
                      const run = openReprocessRuns.find((r) => String(r.id) === String(v));
                      setReprocessForm((f) => ({
                        ...f,
                        reprocessing_batch_id: v,
                        target_production_batch_id: run?.target_production_batch_id
                          ? String(run.target_production_batch_id)
                          : f.target_production_batch_id || form.batch_id || "",
                      }));
                    }}
                  >
                    <SelectTrigger className="mt-1" data-testid="reprocess-run">
                      <SelectValue placeholder="Select open run" />
                    </SelectTrigger>
                    <SelectContent>
                      {openReprocessRuns.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          {r.batch_number}
                          {r.target_batch_code ? ` → ${r.target_batch_code}` : ""}
                          {" · "}
                          cut {Number(r.progress?.total_cut_qtl || 0).toFixed(1)} qtl
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedReprocessRun ? (
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {selectedReprocessRun.destination_type === "BLENDED_INTO_ACTIVE_BATCH"
                        ? "Blended into active batch"
                        : "Dedicated reprocess"}
                      {" · "}
                      packed so far {selectedReprocessRun.progress?.total_packed_bags || 0} bags /{" "}
                      {Number(selectedReprocessRun.progress?.total_packed_qtl || 0).toFixed(3)} qtl
                    </p>
                  ) : null}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Output date
                  </Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={reprocessForm.output_date}
                    onChange={(e) => setReprocessForm((f) => ({ ...f, output_date: e.target.value }))}
                    data-testid="reprocess-date"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Credit to batch
                  </Label>
                  <Select
                    value={reprocessForm.target_production_batch_id || ALL}
                    onValueChange={(v) =>
                      setReprocessForm((f) => ({
                        ...f,
                        target_production_batch_id: v === ALL ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger className="mt-1" data-testid="reprocess-target-batch">
                      <SelectValue placeholder="Target milling batch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>—</SelectItem>
                      {logBatchOptions.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {batchLabel(b)} · {batchStatusLabel(b.status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Category
                  </Label>
                  <Select
                    value={reprocessForm.output_category}
                    onValueChange={(v) => setReprocessForm((f) => ({ ...f, output_category: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="reprocess-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPROCESS_OUTPUT_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Item</Label>
                  <Select
                    value={reprocessForm.item_id}
                    onValueChange={(v) => setReprocessForm((f) => ({ ...f, item_id: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="reprocess-item">
                      <SelectValue placeholder="Select item" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((it) => (
                        <SelectItem key={it.id} value={String(it.id)}>
                          {it.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Bags packed / blended
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    className="mt-1"
                    value={reprocessForm.bags_packed}
                    onChange={(e) => setReprocessForm((f) => ({ ...f, bags_packed: e.target.value }))}
                    required
                    data-testid="reprocess-bags"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Weight / bag (kg)
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="mt-1"
                    value={reprocessForm.pack_size_kg}
                    onChange={(e) => setReprocessForm((f) => ({ ...f, pack_size_kg: e.target.value }))}
                    required
                    data-testid="reprocess-wpb"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={reprocessSubmitting}
                  className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-10"
                  data-testid="reprocess-output-submit"
                >
                  {reprocessSubmitting ? "Saving…" : "Log reprocess output"}
                </Button>
              </form>
            )}
          </SectionCard>

          {selectedReprocessRun ? (
            <SectionCard
              title={`Output log — ${selectedReprocessRun.batch_number}`}
              testId="packing-reprocess-output-log"
            >
              {!(selectedReprocessRun.output_logs || []).length ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No output logged on this run yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[560px]">
                    <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                      <tr>
                        <th className="text-left py-2 px-2">Date</th>
                        <th className="text-left py-2 px-2">Item</th>
                        <th className="text-left py-2 px-2">Category</th>
                        <th className="text-left py-2 px-2">To batch</th>
                        <th className="text-right py-2 px-2">Bags</th>
                        <th className="text-right py-2 px-2">Qtl</th>
                        <th className="text-left py-2 px-2">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...(selectedReprocessRun.output_logs || [])]
                        .slice()
                        .reverse()
                        .map((ln) => (
                          <tr key={ln.id} className="border-b border-border/50">
                            <td className="py-2 px-2 tabular text-xs">{ln.output_date || "—"}</td>
                            <td className="py-2 px-2">{ln.item_name || "—"}</td>
                            <td className="py-2 px-2 text-xs text-muted-foreground">
                              {(ln.output_category || "").replaceAll("_", " ")}
                            </td>
                            <td className="py-2 px-2 font-mono text-xs">{ln.target_batch_code || "—"}</td>
                            <td className="py-2 px-2 text-right tabular">{ln.bags_packed}</td>
                            <td className="py-2 px-2 text-right tabular text-muted-foreground">
                              {Number(ln.quintals_packed || 0).toFixed(3)}
                            </td>
                            <td className="py-2 px-2 text-xs text-muted-foreground">{ln.created_by || "—"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          ) : null}
        </TabsContent>

        <TabsContent value="stock" className="mt-4 space-y-4">
          <SectionCard title="All batch stock (incl. archived)" testId="all-batch-stock">
            <p className="text-xs text-muted-foreground mb-3">
              Remaining packed bags by batch, crop, type, and pack size. Pre-system closing stock uses{" "}
              <span className="font-mono">OS-…</span> batches. Dispatch from{" "}
              <Link to="/sales/loading" className="text-primary underline">Sales → Loading</Link>
              {" · "}
              Full filters on <Link to="/stacks" className="text-primary underline">Stacks & Inventory</Link>.
            </p>
            {loading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : allStock.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No positive stock on any batch.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                  <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">Batch</th>
                      <th className="text-left py-2 px-2">Crop</th>
                      <th className="text-left py-2 px-2">Variety / type</th>
                      <th className="text-left py-2 px-2">Item</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-right py-2 px-2">Kg/bag</th>
                      <th className="text-right py-2 px-2">Bags on hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allStock.map((row) => (
                      <tr key={row.id} className="border-b border-border/50">
                        <td className="py-2 px-2">
                          <span className="font-mono text-xs font-semibold">{row.batch_code || "—"}</span>
                          {row.is_opening_stock_batch ? (
                            <span className="block text-[10px] text-muted-foreground">Pre-system stock</span>
                          ) : null}
                        </td>
                        <td className="py-2 px-2 text-xs">{row.crop_name || "—"}</td>
                        <td className="py-2 px-2 text-xs">{row.type_name || "—"}</td>
                        <td className="py-2 px-2">
                          <span className="font-medium">{row.item_name || "—"}</span>
                          {row.item_code ? (
                            <span className="text-[10px] text-muted-foreground ml-1">({row.item_code})</span>
                          ) : null}
                        </td>
                        <td className="py-2 px-2 text-xs">{batchStatusLabel(row.batch_status)}</td>
                        <td className="py-2 px-2 text-right tabular">{row.weight_per_bag}</td>
                        <td className="py-2 px-2 text-right tabular font-medium">{row.bags_on_hand}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>

        <TabsContent value="history" className="mt-4 space-y-4">
          <SectionCard title="Filters" testId="history-filters">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
                <Select
                  value={historyFilters.batch_id}
                  onValueChange={(v) => setHistoryFilters((f) => ({ ...f, batch_id: v }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="All batches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All batches</SelectItem>
                    {allBatches.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {batchLabel(b)} · {batchStatusLabel(b.status)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">From date</Label>
                <Input
                  className="mt-1"
                  type="date"
                  value={historyFilters.date_from}
                  onChange={(e) => setHistoryFilters((f) => ({ ...f, date_from: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">To date</Label>
                <Input
                  className="mt-1"
                  type="date"
                  value={historyFilters.date_to}
                  onChange={(e) => setHistoryFilters((f) => ({ ...f, date_to: e.target.value }))}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setHistoryFilters({ batch_id: ALL, date_from: "", date_to: "" })}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                  onClick={() => loadHistory().catch((e) => toast.error(extractError(e)))}
                >
                  Apply
                </Button>
              </div>
            </div>
          </SectionCard>

          <SectionCard title={`Packaging history (${logs.length})`} testId="packing-log-table">
            {adminUser ? (
              <p className="text-xs text-muted-foreground mb-3">
                Edit or delete packing entries. Delete is blocked if those bags were already loaded out.
              </p>
            ) : null}
            {loading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : logs.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No packaging entries for these filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-2 px-2">Time</th>
                      <th className="text-left py-2 px-2">Batch</th>
                      <th className="text-left py-2 px-2">Type</th>
                      <th className="text-left py-2 px-2">Item</th>
                      <th className="text-right py-2 px-2">Bags</th>
                      <th className="text-right py-2 px-2">Kg/bag</th>
                      <th className="text-right py-2 px-2">Qtl</th>
                      <th className="text-left py-2 px-2">By</th>
                      {adminUser ? <th className="text-right py-2 px-2">Actions</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((row) => (
                      <tr key={row.id} className="border-b border-border/50">
                        <td className="py-2 px-2 text-xs text-muted-foreground whitespace-nowrap">
                          {formatIndiaDateTime(row.created_at) || "—"}
                        </td>
                        <td className="py-2 px-2 text-xs">{batchLabel(row)}</td>
                        <td className="py-2 px-2 text-xs">
                          {row.entry_kind === "OPENING_STOCK" ? "Opening stock" : "Packing"}
                        </td>
                        <td className="py-2 px-2">{row.item_name}</td>
                        <td className="py-2 px-2 text-right tabular">{row.bags_packed}</td>
                        <td className="py-2 px-2 text-right tabular">{row.weight_per_bag}</td>
                        <td className="py-2 px-2 text-right tabular">{row.total_weight_qtl}</td>
                        <td className="py-2 px-2 text-xs">{row.created_by}</td>
                        {adminUser ? (
                          <td className="py-2 px-2 text-right whitespace-nowrap">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              title="Edit packaging log"
                              aria-label="Edit packaging log"
                              onClick={() => openEdit(row)}
                              data-testid={`edit-pack-log-${row.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-red-700"
                              title="Delete packaging log"
                              aria-label="Delete packaging log"
                              disabled={deletingId === row.id}
                              onClick={() => deleteLog(row)}
                              data-testid={`delete-pack-log-${row.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(editRow)} onOpenChange={(open) => !open && setEditRow(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit packaging log</DialogTitle>
          </DialogHeader>
          {editRow ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Log #{editRow.id}
                {editRow.entry_kind === "OPENING_STOCK" ? " · Opening stock" : " · Packing"}
                {editRow.batch_code ? ` · ${editRow.batch_code}` : ""}
              </p>
              {adminUser ? (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Assign to
                  </Label>
                  <Select
                    value={editForm.assign_mode}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, assign_mode: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-pack-assign-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="batch">Existing milling / archived batch</SelectItem>
                      <SelectItem value="presystem">Pre-system stock (crop + type → OS-…)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              {adminUser && editForm.assign_mode === "presystem" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Crop</Label>
                    <Select
                      value={editForm.crop_id}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, crop_id: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-pack-crop">
                        <SelectValue placeholder="Select crop" />
                      </SelectTrigger>
                      <SelectContent>
                        {crops.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name || c.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Type / variety
                    </Label>
                    <Select
                      value={editForm.type_id}
                      onValueChange={(v) => setEditForm((f) => ({ ...f, type_id: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="edit-pack-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {riceTypes.map((t) => (
                          <SelectItem key={t.id} value={String(t.id)}>
                            {t.name || t.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="sm:col-span-2 text-[10px] text-muted-foreground leading-snug">
                    Moves this log (and its bags) onto the reusable pre-system batch for that crop + type
                    (creates <span className="font-mono">OS-…</span> if needed).
                  </p>
                </div>
              ) : (
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Batch</Label>
                  <Select
                    value={editForm.batch_id}
                    onValueChange={(v) => setEditForm((f) => ({ ...f, batch_id: v }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="edit-pack-log-batch">
                      <SelectValue placeholder="Select batch" />
                    </SelectTrigger>
                    <SelectContent>
                      {logBatchOptions.map((b) => (
                        <SelectItem key={b.id} value={String(b.id)}>
                          {batchLabel(b)} · {batchStatusLabel(b.status)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Change batch if logged against the wrong one. Stock moves with the log.
                  </p>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Item</Label>
                <Select
                  value={editForm.item_id}
                  onValueChange={(v) => setEditForm((f) => ({ ...f, item_id: v }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {items.map((it) => (
                      <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Bags</Label>
                  <Input
                    type="number"
                    min="1"
                    className="mt-1"
                    value={editForm.bags_packed}
                    onChange={(e) => setEditForm((f) => ({ ...f, bags_packed: e.target.value }))}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Kg/bag</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="mt-1"
                    value={editForm.weight_per_bag}
                    onChange={(e) => setEditForm((f) => ({ ...f, weight_per_bag: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Notes</Label>
                <Input
                  className="mt-1"
                  value={editForm.notes}
                  onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditRow(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={editSaving}
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={saveEdit}
            >
              {editSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
